import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MTopBar, SaleBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, cardCls } from "@/components/mobile/parts";
import { MethodGrid, AmountBox, money } from "@/components/mobile/PayPanel";
import { TicketPrint } from "@/components/ticketsale/TicketPrint";
import { useCartActions } from "@/pages/mobile/MProducts";
import { DIRECT_CART } from "@/pages/mobile/MDirectSale";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, isAdminRole, activeMethods, PAY_LABELS } from "@/lib/api";
import { printDoc, printErr } from "@/lib/btprint";
import { cn } from "@/lib/utils";

const r2 = (v) => Math.round(v * 100) / 100;
const Row = ({ l, v, cls, testId }) => <div className={cn("flex justify-between", cls)} data-testid={testId}><span>{l}</span><span className="font-mono">{v}</span></div>;

function directPlan(total, payments, method, recv) {
  const paid = r2(payments.reduce((s, p) => s + p.amount, 0));
  const due = r2(Math.max(0, total - paid));
  const got = money(recv);
  const pay = r2(Math.min(got, due));
  return { paid, due, got, pay, after: r2(Math.max(0, due - got)), partial: got > 0 && got < due - 0.001,
    change: method === "dinheiro" ? r2(Math.max(0, got - due)) : 0, over: method !== "dinheiro" && got > due + 0.001 };
}

function Summary({ cart, total, payments, setPayments, due }) {
  return (
    <div className={cn(cardCls, "space-y-1 p-4 text-sm")} data-testid="m-direct-totals">
      {cart.map((i) => <Row key={i.key} l={`${i.qty}x ${i.name}`} v={brl(i.price * i.qty)} cls="text-slate-600" />)}
      <div className="my-2 border-t border-dashed" />
      <Row l="Total" v={brl(total)} />
      {payments.map((p, i) => (
        <div key={`${p.method}-${i}`} className="flex items-center justify-between text-emerald-700" data-testid={`m-direct-payment-${i}`}>
          <span>{PAY_LABELS[p.method]}</span>
          <span className="flex items-center gap-2 font-mono">- {brl(p.amount)}
            <button onClick={() => setPayments((ps) => ps.filter((_, j) => j !== i))} className="text-red-500" aria-label="Remover" data-testid={`m-direct-payment-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </span>
        </div>
      ))}
      <div className="flex items-end justify-between border-t pt-2"><span className="text-slate-500">A pagar</span><span className="font-display text-3xl font-bold" data-testid="m-direct-remaining">{brl(due)}</span></div>
    </div>
  );
}

function Done({ sale }) {
  return (
    <div className={cn(cardCls, "space-y-3 p-6 text-center")} data-testid="m-direct-done">
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#16a34a]" />
      <p className="font-display text-2xl font-bold">Venda #{sale.number} concluída</p>
      <p className="font-mono text-3xl font-bold">{brl(sale.total)}</p>
      <p className="text-sm text-slate-500">{sale.payments.map((p) => `${PAY_LABELS[p.method]} ${brl(p.amount)}`).join(" + ")}</p>
      {sale.change > 0 && <p className="text-lg font-semibold text-emerald-700" data-testid="m-direct-change">Troco: {brl(sale.change)}</p>}
    </div>
  );
}

export default function MDirectPay() {
  const { session } = useAuth();
  const u = session.user;
  const allowed = isAdminRole(u) ? null : u.ticket_payments;
  const methods = activeMethods(session).filter((m) => m !== "mercadopago" && (!allowed || allowed.includes(m)));
  const { cart, setCart, total } = useCartActions(DIRECT_CART);
  const [payments, setPayments] = useState([]);
  const [method, setMethod] = useState(methods.includes("dinheiro") ? "dinheiro" : methods[0]);
  const [recv, setRecv] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const nav = useNavigate();
  const printTickets = (sale) => printDoc(session, "ticket", `Tickets · Venda #${sale.number}`, { sale });
  if (!cart.length && !done) return <Navigate to="/m/venda-direta/carrinho" replace />;
  const plan = directPlan(total, payments, method, recv);
  const ready = plan.due <= 0.001 || (plan.got >= plan.due - 0.001 && !plan.over);
  const addOther = () => { setPayments((ps) => [...ps, { method, amount: plan.pay }]); setRecv(""); };
  const submit = async () => {
    const all = plan.due <= 0.001 ? payments : [...payments, { method, amount: method === "dinheiro" ? plan.got : plan.pay }];
    setBusy(true);
    try {
      const { data } = await api.post("/ticket-sales", { items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, options: i.options, notes: i.notes })), payments: all, discount: 0 });
      setCart([]); setDone(data);
      printTickets(data).catch((e) => toast.error(printErr(e)));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reprint = () => printTickets(done).then((w) => w === "none" && window.print()).catch((e) => toast.error(printErr(e)));
  const label = ready ? "Finalizar venda" : plan.partial ? `Falta ${brl(plan.after)}` : "Digite o valor";
  return (
    <>
      <MTopBar title="Pagamento" back={done ? "/m/pdv" : "/m/venda-direta/carrinho"} />
      <SaleBar />
      <MPage>
        {done ? <Done sale={done} /> : (<>
          <Summary cart={cart} total={total} payments={payments} setPayments={setPayments} due={plan.due} />
          {plan.due > 0 && (<>
            <MethodGrid methods={methods} method={method} setMethod={setMethod} />
            <AmountBox recv={recv} setRecv={setRecv} plan={plan} method={method}
              partialHint={<>Falta <b>{brl(plan.after)}</b>. Toque em <b>Adicionar outra forma</b> para completar com outra forma de pagamento.</>} />
            {plan.partial && !plan.over && (
              <button onClick={addOther} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#1565c0] text-sm font-semibold text-[#1565c0]" data-testid="m-direct-add-other-btn">
                <Plus className="h-4 w-4" />Adicionar outra forma · {PAY_LABELS[method]} {brl(plan.pay)}
              </button>
            )}
          </>)}
        </>)}
      </MPage>
      {!done && (
        <Footer testId="m-direct-pay-footer">
          <button onClick={submit} disabled={!ready || busy} className="flex w-full items-center justify-between bg-[#16a34a] px-5 py-4 text-white hover:bg-[#15803d] disabled:opacity-60" data-testid="m-direct-finish-btn">
            <span className="flex items-center gap-2 font-display text-lg font-semibold">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Receipt className="h-5 w-5" />}{label}</span>
            <span className="font-mono text-xl font-bold" data-testid="m-direct-finish-total">{brl(total)}</span>
          </button>
        </Footer>
      )}
      <TicketPrint sale={done} onClose={() => nav("/m/pdv")} onPrint={reprint} />
    </>
  );
}
